import { values } from 'lodash';
import React, { Component } from 'react';
import classNames from 'classnames';
import ErrorModal from 'components/Practice/shared/ErrorModal';
import recordingPermissionErrorTypes from 'constants/recordingPermissionErrorTypes.js';
import trackEvent from 'misc/trackEvent';
import { Button, Icon } from 'ui';
import RecordingOptions from '../media_recording/RecordingOptions';

class RecordMenu extends Component {
  constructor(props) {
    super(props);
    this.state = {
      errorType: null,
      loading: true,
      screen: this.props.browserSupport.screen,
      webcam: this.props.browserSupport.webcam,
      recordedUrl: null,
      videoUploading: false,
      recordingWasAborted: false,
      recording: false,
    };
    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handleRecord = this.handleRecord.bind(this);
    this.recordingType = this.recordingType.bind(this);
    this.showErrorModal = this.showErrorModal.bind(this);
    this.handleResetError = this.handleResetError.bind(this);
    this.trackRecordingData = this.trackRecordingData.bind(this);
    this.trackVideoSubmitted = this.trackVideoSubmitted.bind(this);
    this.trackRecordingEvents = this.trackRecordingEvents.bind(this);
    this.trackRecordingInitiated = this.trackRecordingInitiated.bind(this);
  }

  componentDidMount() {
    window.hippoWidget.onLoad(() => {
      this.setState({ loading: false });
    });
  }

  trackRecordingEvent = (event, data) => {
    if (this.props.isExampleRecording) return;
    trackEvent(event, data);
  };

  trackingEventParameters = () => ({
    lesson_section_element_id: this.props.id,
    progress_id: null,
    question_id: null,
    recording_type: this.recordingType(),
  });

  trackRecordingEvents = data => {
    this.trackRecordingInitiated(data);
    this.trackRecordingData(data);
  };

  trackRecordingData = data => {
    this.trackRecordingEvent('recording_metadata', {
      asset_id: data.asset_id,
      token: data.token,
    });
  };

  trackRecordingInitiated(data) {
    const eventData = {
      ...this.trackingEventParameters(),
      token: data.token,
    };
    this.trackRecordingEvent('hippo_video/recording_initiated', eventData);
  }

  trackRecordingStarted = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    this.trackRecordingEvent('hippo_video/recording_started', eventData);
  };

  trackRecordingAborted = () => {
    const eventData = {
      lesson_section_element_id: this.props.id,
      question_id: null,
      progress_id: null,
      recording_type: this.recordingType(),
    };
    this.trackRecordingEvent('hippo_video/recording_aborted', eventData);
  };

  trackRecordingStopped = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    this.trackRecordingEvent('hippo_video/recording_stopped', eventData);
  };

  trackSourceUploaded = () => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: false,
    };
    this.trackRecordingEvent('hippo_video/source_uploaded', eventData);
  };

  recordingType = () => {
    if (this.state.webcam && this.state.screen) {
      return 'webcam screen';
    }
    if (this.state.webcam) {
      return 'webcam';
    }
    return 'screen';
  };

  trackVideoCancelled = () => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: false,
    };
    this.trackRecordingEvent('hippo_video/recording_cancelled', eventData);
  };

  trackVideoSubmitted(isUpload) {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: isUpload,
    };
    this.trackRecordingEvent('hippo_video/video_submitted', eventData);
  }

  trackPermissionDenied = errorType => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: false,
      error_type: errorType,
    };
    this.trackRecordingEvent('hippo_video/permission_denied', eventData);
  };

  handleOptionChange(e) {
    this.setState({ [e.target.name]: e.target.checked });
  }

  handleResetError() {
    this.setState({ errorType: null });
  }

  handleRecord() {
    const recordingConfig = {
      screenRecord: this.state.screen,
      audio: true,
      webCam: this.state.webcam,
      resolution: '1080',
      tabRecording: false,
      separateLayer: false,
      fromPopup: !this.state.screen,
      initiator: 'generic_embed',
    };
    window.hippoWidget.on('record_error', this.showErrorModal);
    window.hippoWidget.startRecording(
      recordingConfig,
      this.props.currentUserId,
    );
    window.hippoWidget.on('source_uploaded', this.sourceUploaded);
    window.hippoWidget.on('record_initiated', this.trackRecordingEvents);
    window.hippoWidget.on('record_started', this.recordingStarted);
    window.hippoWidget.on('record_aborted', this.recordingAborted);
    window.hippoWidget.on('record_stopped', this.recordingStopped);
    window.hippoWidget.on('record_aborted', this.recordingAborted);
    window.hippoWidget.on('video_submitted', this.videoSubmitted);
    window.hippoWidget.on('video_cancelled', this.videoCancelled);
  }

  recordingStarted = () => {
    this.setState({
      recording: true,
    });
    this.trackRecordingStarted();
  };

  recordingAborted = () => {
    this.setState({
      recordingWasAborted: true,
      recording: false,
    });
    this.trackRecordingAborted();
  };

  videoCancelled = () => {
    this.setState({
      videoUploading: false,
      recording: false,
    });
    this.trackVideoCancelled();
  };

  recordingStopped = () => {
    this.setState(state => ({
      videoUploading: !state.recordingWasAborted,
      recordingWasAborted: false,
      recording: false,
    }));
    this.trackRecordingStopped();
  };

  sourceUploaded = () => {
    const { recordedUrl } = this.state;
    if (recordedUrl) {
      this.props.onSubmit(`${recordedUrl}?type=lessonly`);
    }
    this.setState({
      videoUploading: false,
    });
    this.trackSourceUploaded();
  };

  videoSubmitted = e => {
    this.setState(
      {
        recordedUrl: e.recorded_url,
        recording: false,
      },
      () => {
        if (!this.state.videoUploading) {
          this.props.onSubmit(`${this.state.recordedUrl}?type=lessonly`);
        }
      },
    );
    this.trackVideoSubmitted(false);
  };

  trackRecordingError = errorType => {
    const eventData = {
      ...this.trackingEventParameters(),
      upload: false,
      error_type: errorType,
    };
    this.trackRecordingEvent('hippo_video/recording_error', eventData);
  };

  showErrorModal(response) {
    const errorType = response.error;

    if (Object.values(recordingPermissionErrorTypes).includes(errorType)) {
      this.trackPermissionDenied(errorType);
    } else {
      this.trackRecordingError(errorType);
    }
    if (
      errorType !==
      recordingPermissionErrorTypes.HIPPO_SCREEN_PERMISSION_DENIED_ERROR
    ) {
      this.setState({ errorType });
    }
  }

  render() {
    const { loading, errorType, videoUploading, recording, screen, webcam } =
      this.state;
    const {
      toggleRecordMenu,
      browserSupport,
      isExampleRecording = false,
    } = this.props;
    // If no recording types are checked, we need to disable the record button
    const noCheckedOptions = !this.state.screen && !this.state.webcam;
    // If all values in the browserSupport object are false, we need to disable the record button
    const isRecordingDisabled =
      values(this.props.browserSupport).every(supported => !supported) ||
      noCheckedOptions;

    if (videoUploading) {
      return (
        <div>{t('lessons.editor.media_recording.processing_message')}</div>
      );
    }

    if (recording) {
      return <div>{t('lessons.editor.media_recording.recording')}</div>;
    }

    return (
      <div
        className={classNames(
          { 'w-fill': isExampleRecording },
          'EditorElement-videoRecording d-f fd-c',
        )}
      >
        <div className="EditorElement-back d-f fd-r c-brand-blue ai-c">
          <Icon name="back" className="mr-8" />
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <div className="mt-4 c-p" onClick={toggleRecordMenu}>
            {t('common.back')}
          </div>
        </div>
        <div className="EditorElement-recordingButtons d-f ai-c fg-1 jc-c fd-c bg-gray-lightest">
          <Button
            className="mb-16"
            primary
            loading={loading}
            onClick={this.handleRecord}
            disabled={loading || isRecordingDisabled}
          >
            {t('lessons.editor.media_recording.record')}
          </Button>
          <RecordingOptions
            onChange={e => this.handleOptionChange(e)}
            browserSupport={browserSupport}
            webcam={webcam}
            screen={screen}
            defaultAudio
          />
          <span className="fs-i mt-24">
            {t('lessons.editor.embeds.video.audio_note')}
          </span>
        </div>
        {this.state.errorType && (
          <ErrorModal errorType={errorType} onAction={this.handleResetError} />
        )}
      </div>
    );
  }
}

export default RecordMenu;
